import { useCallback } from "react";

import { useCurrentAccount } from "@/providers/account";
import { useNavigate } from "@tanstack/react-location";

/**
 * Navigate to a path scoped to the current account slug.
 *
 * Lives here rather than in src/routes.tsx because leaf modules need it:
 * `features/model-tool/editor/tabs/tabNavigation.ts` importing it from
 * `@/routes` made the tabs barrel and `routes.tsx` mutually dependent, which
 * Rollup could only resolve by placing a re-export binding in a chunk that may
 * not be initialised yet ("will likely lead to broken execution order").
 * `src/router/` deliberately imports nothing from `@/routes`, so it is safe for
 * leaves to depend on.
 */
export function useNavigateWithSlug() {
  const account = useCurrentAccount();
  const navigate = useNavigate();

  const slug = account?.slug;

  return useCallback(
    (
      useNavigateParams: Parameters<ReturnType<typeof useNavigate>>[0],
      withSlug: boolean = true,
    ) => {
      const prependSlug = (to: string, slug: string) => {
        return `/${slug}${to.startsWith("/") ? to : `/${to}`}`;
      };
      navigate({
        ...useNavigateParams,
        to:
          withSlug && slug && typeof useNavigateParams.to === "string"
            ? prependSlug(useNavigateParams.to, slug)
            : useNavigateParams.to,
      });
    },
    [navigate, slug],
  );
}
